import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';
import api from '../api';
import listings from '../api/listings';
import { LISTING_TYPE } from '../constants/listingAttributes';
import { TRANSITIONS_TO_REQUESTED } from '../transactions/transactionProcessBooking';
import { GoogleTagManagerHandler } from '../analytics/handlers';

const USER_INCLUDES = {
  include: ['profileImage', 'stripeAccount'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.square-xsmall',
    'variants.square-xsmall2x',
  ],
  'imageVariant.square-xsmall': sdkUtil.objectQueryString({
    w: 40,
    h: 40,
    fit: 'crop',
  }),
  'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
    w: 80,
    h: 80,
    fit: 'crop',
  }),
};

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

const FETCH_CURRENT_USER_NON_FREE_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NON_FREE_LISTINGS_REQUEST';
const FETCH_CURRENT_USER_NON_FREE_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NON_FREE_LISTINGS_SUCCESS';
const FETCH_CURRENT_USER_NON_FREE_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_NON_FREE_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const CURRENT_USER_SAVED_LISTINGS_REQUEST = 'app/user/CURRENT_USER_SAVED_LISTINGS_REQUEST';
export const CURRENT_USER_SAVED_LISTINGS_SUCCESS = 'app/user/CURRENT_USER_SAVED_LISTINGS_SUCCESS';
export const CURRENT_USER_SAVED_LISTINGS_ERROR = 'app/user/CURRENT_USER_SAVED_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_REQUEST =
  'app/user/FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_REQUEST';
export const FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_SUCCESS =
  'app/user/FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_SUCCESS';
export const FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_ERROR =
  'app/user/FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_ERROR';
export const DECREMENT_CURRENT_USER_MESSAGE_NOTIFICATION_COUNT =
  'app/user/DECREMENT_CURRENT_USER_MESSAGE_NOTIFICATION_COUNT';

export const UPDATE_CURRENT_USER_WALLET_BALANCES = 'app/user/UPDATE_CURRENT_USER_WALLET_BALANCES';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,

  currentUserHasListings: false,
  currentUserHasListingsError: null,

  currentUserHasNonFreeListings: false,

  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,

  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  currentUserListing: null,
  currentUserListingFetched: false,
  currentUserSavedListingsInProgress: false,
  currentUserSavedListingsError: null,

  currentUserMessageNotificationCount: 0,
  currentUserMessageNotificationError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserHasNonFreeListings: false,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        currentUserListing: null,
        currentUserListingFetched: false,
      };

    case UPDATE_CURRENT_USER_WALLET_BALANCES: {
      // Update wallet balances in current user's privateData without fetching
      if (!state.currentUser) {
        return state;
      }
      
      const updatedUser = {
        ...state.currentUser,
        attributes: {
          ...state.currentUser.attributes,
          profile: {
            ...state.currentUser.attributes.profile,
            privateData: {
              ...state.currentUser.attributes.profile.privateData,
              walletBalances: action.payload,
            },
          },
        },
      };
      
      return { ...state, currentUser: updatedUser };
    }

    case FETCH_CURRENT_USER_NON_FREE_LISTINGS_REQUEST:
      return state;
    case FETCH_CURRENT_USER_NON_FREE_LISTINGS_SUCCESS:
      return { ...state, currentUserHasNonFreeListings: payload };
    case FETCH_CURRENT_USER_NON_FREE_LISTINGS_ERROR:
      return state;

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserHasListings: payload.hasListings,
        currentUserListing: payload.listing,
        currentUserListingFetched: true,
      };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case CURRENT_USER_SAVED_LISTINGS_REQUEST:
      return {
        ...state,
        currentUserSavedListingsInProgress: true,
        currentUserSavedListingsError: null,
      };
    case CURRENT_USER_SAVED_LISTINGS_SUCCESS:
      return {
        ...state,
        currentUserSavedListingsInProgress: false,
        currentUser: mergeCurrentUser(state.currentUser, payload),
      };
    case CURRENT_USER_SAVED_LISTINGS_ERROR:
      return {
        ...state,
        currentUserSavedListingsInProgress: false,
        currentUserSavedListingsError: payload,
      };

    case FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_REQUEST:
      return { ...state, currentUserMessageNotificationError: null };
    case FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_SUCCESS:
      return {
        ...state,
        currentUserMessageNotificationCount: payload,
      };
    case FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserMessageNotificationError: payload };
    case DECREMENT_CURRENT_USER_MESSAGE_NOTIFICATION_COUNT:
      const count = state.currentUserMessageNotificationCount;
      return { ...state, currentUserMessageNotificationCount: count > 0 ? count - 1 : count };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

export const updateCurrentUserWalletBalances = walletBalances => ({
  type: UPDATE_CURRENT_USER_WALLET_BALANCES,
  payload: walletBalances,
});

const fetchCurrentUserNonFreeListingsRequest = () => ({
  type: FETCH_CURRENT_USER_NON_FREE_LISTINGS_REQUEST,
});

const fetchCurrentUserNonFreeListingsSuccess = payload => ({
  type: FETCH_CURRENT_USER_NON_FREE_LISTINGS_SUCCESS,
  payload,
});

const fetchCurrentUserNonFreeListingsError = e => ({
  type: FETCH_CURRENT_USER_NON_FREE_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = (hasListings, listing) => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings, listing },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const currentUserSavedListingRequest = () => ({ type: CURRENT_USER_SAVED_LISTINGS_REQUEST });

export const currentUserSavedListingSuccess = user => ({
  type: CURRENT_USER_SAVED_LISTINGS_SUCCESS,
  payload: user,
});

export const currentUserSavedListingError = e => ({
  type: CURRENT_USER_SAVED_LISTINGS_ERROR,
  payload: e,
  error: true,
});

const fetchCurrentUserMessageNotificationRequest = () => ({
  type: FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_REQUEST,
});
export const fetchCurrentUserMessageNotificationSuccess = notificaitonCount => ({
  type: FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_SUCCESS,
  payload: notificaitonCount,
});
const fetchCurrentUserMessageNotificationError = e => ({
  type: FETCH_CURRENT_USER_MESSAGE_NOTIFICATION_ERROR,
  error: true,
  payload: e,
});

export const decrementCurrentUserMessageNotificationCount = () => ({
  type: DECREMENT_CURRENT_USER_MESSAGE_NOTIFICATION_COUNT,
});

// ================ Thunks ================ //

const fetchCurrentUserNonFreeListings = userId => (dispatch, getState) => {
  dispatch(fetchCurrentUserNonFreeListingsRequest());

  const { partner } = getState().ui;

  const params = {
    $limit: 1,
    $page: 1,
    'attributes.publicData.type': {
      $ne: LISTING_TYPE.FREE,
    },
    authorId: userId,
    'attributes.state': { $ne: LISTING_STATE_DRAFT },
  };

  return listings
    .fetchListings(params, partner?.key)
    .then(res => {
      const { items } = res.data;

      dispatch(fetchCurrentUserNonFreeListingsSuccess(!!items[0]));
    })
    .catch(e => dispatch(fetchCurrentUserNonFreeListingsError(e)));
};

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 1,
  };

  dispatch(fetchCurrentUserNonFreeListings(currentUser.id.uuid));

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;
      const listing = hasListings ? response.data.data[0] : null;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings, listing));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const { uuid: userId } = getState().user.currentUser.id;

  const apiQueryParams = {
    only: 'sale',
    last_transitions: TRANSITIONS_TO_REQUESTED,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));

  const params = {
    $page: 1,
    $limit: 1,
    $sort: 'attributes.metadata.lastMessage.seenBy',
  };

  const userTypeParams = {
    provider: {
      'attributes.metadata.lastMessage.seenBy': { $ne: 'provider' },
      providerId: { $eq: `${userId}` },
    },
    customer: {
      'attributes.metadata.lastMessage.seenBy': { $ne: 'customer' },
      customerId: { $eq: `${userId}` },
    },
  };

  dispatch(fetchCurrentUserMessageNotificationRequest());

  const providerMessages = api.transactions
    .fetchTransactions({
      ...params,
      ...userTypeParams.provider,
    })
    .then(res => {
      return res.data.totalItems || 0;
    });

  const customerMessages = api.transactions
    .fetchTransactions({
      ...params,
      ...userTypeParams.customer,
    })
    .then(res => {
      return res.data.totalItems || 0;
    });

  Promise.all([providerMessages, customerMessages])
    .then(values => {
      const totalNoSeenMessages = values[0] + values[1];
      dispatch(fetchCurrentUserMessageNotificationSuccess(totalNoSeenMessages));
    })
    .catch(e => {
      dispatch(fetchCurrentUserMessageNotificationError(e));
    });
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || USER_INCLUDES;

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      GoogleTagManagerHandler.addUserContext(currentUser);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const currentUserSavedListings = (savedListings = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserSavedListingRequest());

  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserSavedListingSuccess(null));
    return Promise.resolve({});
  }

  const params = {
    privateData: {
      savedListings,
    },
  };

  return sdk.currentUser
    .updateProfile(params, { ...USER_INCLUDES, expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      return entities[0];
    })
    .then(currentUser => {
      dispatch(currentUserSavedListingSuccess(currentUser));
    })
    .catch(e => dispatch(currentUserSavedListingError(storableError(e))));
};
